/**
 * Session Cleanup Utilities
 *
 * Handles detecting tab closure vs. page refresh using sessionStorage.
 * sessionStorage persists within a tab across refreshes but clears when the tab closes.
 *
 * Authentication tokens are stored in HttpOnly cookies, which are sent automatically
 * by the browser. On tab close, the server-side session remains valid until the
 * refreshToken expires or the user logs out explicitly.
 */

const TAB_ID_KEY = 'app_tab_id';

/**
 * Get or create a unique tab ID stored in sessionStorage.
 * Same ID persists across page refreshes within the same tab.
 * New tab = new ID (because sessionStorage is cleared on tab close).
 */
const getOrCreateTabId = (): string => {
  let tabId = sessionStorage.getItem(TAB_ID_KEY);

  if (!tabId) {
    tabId = crypto.randomUUID();
    sessionStorage.setItem(TAB_ID_KEY, tabId);
    // console.log('[SessionCleanup] New tab detected, ID:', tabId);
  }

  return tabId;
};

/**
 * Clear HttpOnly authentication cookies.
 *
 * Note:
 * HttpOnly cookies CANNOT be cleared from JavaScript.
 * They must be cleared by the backend logout endpoint.
 * This is a placeholder that logs the intent; actual clearing happens server-side.
 */
export const clearAuthCookies = (): void => {
  console.log(
    '[SessionCleanup] HttpOnly cookies must be cleared by backend logout endpoint'
  );
};

/**
 * Clear client-side authentication/session data.
 *
 * Note:
 * HttpOnly cookies cannot be cleared from JavaScript.
 * They must be cleared by the backend.
 */
export const clearSessionData = (): void => {
  // console.log('[SessionCleanup] Clearing session data');

  sessionStorage.clear();

  // console.log('[SessionCleanup] sessionStorage cleared');
};

/**
 * Initialize session cleanup listener.
 *
 * Initializes tab ID detection. Does NOT call logout on beforeunload
 * because beforeunload fires on both refresh and tab close.
 *
 * The server handles session expiry via cookie TTLs.
 */
export const initializeSessionCleanupListener = (): (() => void) => {
  // Initialize tab ID detection on app load
  getOrCreateTabId();

  const handleBeforeUnload = () => {
    // beforeunload fires on BOTH refresh and tab close
    // We cannot reliably distinguish them here
    // Server will handle session cleanup via cookie expiry
    // console.log('[SessionCleanup] beforeunload event (refresh or tab close)');
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', handleBeforeUnload);
    // console.log('[SessionCleanup] Session cleanup listener initialized');
  }

  return () => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // console.log('[SessionCleanup] Session cleanup listener removed');
    }
  };
};
